import { useEffect, useMemo, useRef, useState } from "react";
import { Map as CoreMap, MapRef } from "react-map-gl";
import { MapboxOverlay, MapboxOverlayProps } from "@deck.gl/mapbox/typed";
import { useControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Box, LinearProgress } from "@mui/material";
import { MapProps } from "../../../../../../../../Components/Map/types";
import {
  MAPBOX_STYLE,
  MAPBOX_TOKEN,
} from "../../../../../../../../Components/Map/enums";
import useQueryState from "../../../../../../../../hooks/useQueryState";
import useCountries from "../../../../../../../../hooks/useCountries";
import bbox from "@turf/bbox";
import { Feature, FeatureCollection } from "geojson";
import { GeoJsonLayer } from "@deck.gl/layers/typed";
import type { Layer } from "@deck.gl/core/typed";

/* ---------------- DeckGL Overlay ---------------- */
function DeckGLOverlay(props: MapboxOverlayProps & { interleaved?: boolean }) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps({ ...props, getCursor: () => "crosshair" });
  return null;
}

const PreviewMap = ({
  children,
  width = "100vw",
  height,
  files = [],
  areaOfInterest,
}: MapProps) => {
  const mapRef = useRef<MapRef | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const {
    queryState: { country },
  } = useQueryState();

  const {
    getCountryAdministrativeUnit: {
      data: geoUnit,
      isFetching: isLoadingCountry,
    },
  } = useCountries({
    country,
    aggregationLevel: "1",
    enabled: true,
  });

  const bounds = useMemo(() => {
    if (!geoUnit?.features?.length) return undefined;

    const featureCollection: FeatureCollection = {
      type: "FeatureCollection",
      features: geoUnit.features as Feature[],
    };

    return bbox(featureCollection);
  }, [geoUnit, files.length]);

  useEffect(() => {
    if (!bounds || !mapLoaded || !mapRef.current) return;

    const map = mapRef.current.getMap();

    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      {
        maxZoom: 8,
        duration: 1000,
      }
    );
  }, [bounds, mapLoaded]);

  const geoJsonLayer = useMemo<Layer[]>(
    () =>
      areaOfInterest
        ? [
            new GeoJsonLayer({
              id: `geojson-layer-${files[0]?.name ?? "aoi"}`,
              data: areaOfInterest.features,
              pickable: false,
              stroked: true,
              filled: true,
              getFillColor: [224, 64, 251, 51], // #E040FB33
              getLineColor: [224, 64, 251, 255], // #E040FB
              lineWidthMinPixels: 1,
            }),
          ]
        : [],
    [areaOfInterest, files]
  );

  return (
    <CoreMap
      id="previewMap"
      ref={mapRef}
      mapStyle={MAPBOX_STYLE}
      mapboxAccessToken={MAPBOX_TOKEN}
      cursor="crosshair"
      onMoveStart={() => setIsLoading(true)}
      onMoveEnd={() => setIsLoading(false)}
      onZoomStart={() => setIsLoading(true)}
      onZoomEnd={() => setIsLoading(false)}
      onLoad={() => setMapLoaded(true)}
      style={{ width, height, borderRadius: "4px", zIndex: 1 }}
    >
      {children}

      {(isLoading || isLoadingCountry) && (
        <Box sx={{ position: "absolute", zIndex: 10, top: 0, width: "100%" }}>
          <LinearProgress />
        </Box>
      )}

      <DeckGLOverlay
        layers={geoJsonLayer}
        interleaved
        getTooltip={({ object }) => {
          if (!object) return null;

          const props = (object as any).properties ?? {};
          return { text: props.geounit ?? "" };
        }}
      />
    </CoreMap>
  );
};

export default PreviewMap;
